import { Observable, type ReadonlyObservable } from "@/lib/studio/observable";
import { cameraPairingUrl } from "../camera-link";
import {
  MediaTiming,
  videoContent,
  type MediaContent,
  type MediaPlayout,
  type MediaTimingChange,
} from "../media-timing";
import { openCameraTrack } from "./camera-capture";
import { DEFAULT_FRAMING, subjectIn, type CameraFraming, type CameraFramings } from "./camera-framing";
import { FramedCamera } from "./framed-camera";
import type {
  LocalCamera,
  TransportSource,
  VideoControl,
  VideoPairing,
  VideoPairingLink,
  VideoQuality,
  VideoSource,
  VideoSubject,
  VideoTransport,
} from "./video-types";

export interface VideoEngineOptions {
  transport: VideoTransport;
  /** Couper un appareil appairé. Sans lui, `remove` ne coupe rien. */
  control?: VideoControl;
  /** Créer et retirer un code d'appairage. Sans lui, `pairDevice` échoue. */
  pairing?: VideoPairing;
  /**
   * La qualité reçue d'une source qu'on n'a pas regardée autrement : `low` par
   * défaut, la 360p d'une vignette. Le 720p du milieu est la couche de
   * l'enregistreur (voir `cameraPublishOptions`).
   */
  defaultQuality?: VideoQuality;
  /**
   * Le calage de la séance, partagé avec le moteur audio : un visage s'y cale
   * sur la voix de son compte quand la parole passe au plus tôt. Sans lui, le
   * moteur a le sien.
   */
  timing?: MediaTiming;
  /** Ouvre une caméra. Injecté par les tests. */
  openCamera?: (deviceId: string | null) => Promise<MediaStreamTrack>;
  /** L'origine du lien d'appairage : celle de la page par défaut. */
  origin?: () => string;
}

/** Levée par un geste que la fermeture du moteur, ou la fin de la séance, a interrompu. */
export class VideoEngineClosedError extends Error {
  constructor() {
    super("Moteur vidéo fermé.");
    this.name = "VideoEngineClosedError";
  }
}

/** Ce que le moteur applique à une source reçue. */
export interface VideoPlayoutDiagnostic {
  id: string;
  subject: VideoSubject;
  /** Ce qu'elle porte : du jeu, ou de la parole. */
  content: MediaContent;
  /** La retenue posée, derrière la prise de chaque image. */
  holdMs: number;
  /** Ce qu'une image met à se montrer sans retenue ; `null` sans mesure. */
  latencyMs: number | null;
}

type DeviceArrivedListener = (source: VideoSource) => void;

/** Une source suivie par le calage, et de quoi cesser de la suivre. */
interface Followed {
  owner: string;
  unfollow: () => void;
}

/**
 * Le moteur vidéo d'une séance : publier sa caméra, recevoir celles de la
 * salle, appairer un appareil qui filme pour soi, et caler chaque image — des
 * mains toujours sur les notes, un visage comme la séance le veut (voir
 * `lib/live/media-timing.ts`).
 *
 * Il ne décide ni qui filme, ni combien de sources sont permises, ni laquelle
 * montrer : ce sont les règles du contexte — le récital aujourd'hui, le cours
 * demain —, qui s'appuient sur cette API. Il ne connaît ni LiveKit ni React. Voir
 * `docs/spec/video-engine.md`.
 */
export class VideoEngine {
  private readonly transport: VideoTransport;
  private readonly control: VideoControl | null;
  private readonly pairingPort: VideoPairing | null;
  private readonly defaultQuality: VideoQuality;
  private readonly timing: MediaTiming;
  private readonly ownsTiming: boolean;
  private readonly openCamera: (deviceId: string | null) => Promise<MediaStreamTrack>;
  private readonly origin: () => string;
  private readonly detach: () => void;
  private readonly stopTiming: () => void;

  private readonly _sources = new Observable<readonly VideoSource[]>([]);
  /**
   * Les sources telles que le transport les donne, sans ce qu'elles filment.
   * Gardées pour reprojeter quand les cadrages changent sans que la salle ait
   * bougé.
   */
  private incoming: readonly TransportSource[] = [];
  /** Les cadrages publiés par la séance. Voir {@link VideoEngine.setFramings}. */
  private framings: CameraFramings = {};
  /**
   * Le cadrage posé sur la webcam de ce poste.
   *
   * La même règle que pour un téléphone appairé, et pour la même raison : ce
   * qu'on écarte du champ doit disparaître **avant l'encodeur**, sans quoi
   * l'image entière part quand même dans la salle et dans l'enregistrement.
   * Voir {@link FramedCamera}.
   */
  private cameraFramed: FramedCamera | null = null;
  private readonly _camera = new Observable<LocalCamera | null>(null);
  private readonly _pairing = new Observable<VideoPairingLink | null>(null);
  private readonly deviceListeners = new Set<DeviceArrivedListener>();

  /** La qualité demandée par source ; les autres reçoivent `defaultQuality`. */
  private readonly qualities = new Map<string, VideoQuality>();
  /** La retenue donnée au transport pour chaque source, la dernière fois qu'il l'a demandée. */
  private readonly holds = new Map<string, number>();
  /** Les visages suivis par le calage, par source. */
  private readonly followed = new Map<string, Followed>();
  /**
   * Les appareils appairés déjà vus. Un appareil qui revient d'une coupure garde
   * sa piste et n'est pas un nouveau venu ; un appareil qui se met à filmer en
   * apporte une neuve.
   */
  private readonly seenDevices = new Set<string>();
  /** Les coupures demandées au service, en cours ou réussies, par source. Voir `remove`. */
  private readonly removals = new Map<string, Promise<boolean>>();
  private cameraTrack: MediaStreamTrack | null = null;
  /** Rideau baissé : la caméra de ce poste est ouverte mais hors de la salle. Voir `setCurtain`. */
  private curtain = false;
  /** Le dernier geste en cours : le suivant l'attend. Voir `serialize`. */
  private gestures: Promise<unknown> = Promise.resolve();
  /** Change à chaque `release` : un geste commencé pour une séance ne finit pas dans la suivante. */
  private generation = 0;
  private closed = false;

  constructor(options: VideoEngineOptions) {
    this.transport = options.transport;
    this.control = options.control ?? null;
    this.pairingPort = options.pairing ?? null;
    this.defaultQuality = options.defaultQuality ?? "low";
    this.timing = options.timing ?? new MediaTiming();
    this.ownsTiming = options.timing === undefined;
    this.openCamera = options.openCamera ?? openCameraTrack;
    this.origin = options.origin ?? (() => window.location.origin);
    this.detach = this.transport.attach({
      sources: (next) => this.receive(next),
      holdFor: (source) => this.applyHold(source),
      qualityFor: (source) => this.qualities.get(source.id) ?? this.defaultQuality,
      disconnected: () => this.release(),
    });
    this.stopTiming = this.timing.onChange((change) => this.retime(change));

    // Un calage partagé peut déjà tenir l'horloge : ce qui part se date avec elle
    // dès maintenant.
    if (this.timing.playout.clockOffsetMs !== null) {
      this.transport.setClock(this.timing.playout.clockOffsetMs);
    }
  }

  /** Les sources de la salle, locales comprises, de la plus ancienne à la plus récente. */
  get sources(): ReadonlyObservable<readonly VideoSource[]> {
    return this._sources;
  }

  /** La caméra que ce poste publie, ou `null`. */
  get camera(): ReadonlyObservable<LocalCamera | null> {
    return this._camera;
  }

  /** Le lien d'appairage en cours, tant qu'aucun appareil ne l'a pris. */
  get pairing(): ReadonlyObservable<VideoPairingLink | null> {
    return this._pairing;
  }

  /** La référence de propriétaire de ce poste, une fois connecté. */
  get self(): string | null {
    return this.transport.localOwner();
  }

  /**
   * Prévient quand un appareil appairé se met à filmer pour ce poste. Rend de
   * quoi ne plus l'être.
   */
  onDeviceArrived(listener: DeviceArrivedListener): () => void {
    this.deviceListeners.add(listener);

    return () => {
      this.deviceListeners.delete(listener);
    };
  }

  /**
   * Ouvre la caméra de ce poste et la publie, à la place de la précédente.
   *
   * Ce qu'elle filme ne se déclare pas ici : c'est la séance qui le dit, dans
   * sa table de cadrage — des mains tant que personne n'a rien dit. Voir
   * {@link setFramings}.
   *
   * La piste appartient au moteur : il l'arrête à `stopCamera`, à la fermeture,
   * et quand elle se termine d'elle-même. Un voyant resté allumé après
   * « Couper » serait la pire façon d'apprendre que la caméra filmait encore.
   * Le refus du navigateur remonte tel quel.
   */
  startCamera(options: { deviceId?: string | null } = {}): Promise<LocalCamera> {
    return this.serialize(() => this.openAndPublish(options.deviceId ?? null));
  }

  /** Retire la caméra de la salle et l'éteint. Sans effet s'il n'y en a pas. */
  stopCamera(): Promise<void> {
    return this.serialize(() => this.unpublishCamera());
  }

  /**
   * Baisse ou relève le rideau sur la caméra de ce poste : elle sort de la
   * salle, mais reste ouverte.
   *
   * # Sortir plutôt que mettre en sourdine
   *
   * Une publication en sourdine continue d'exister et d'envoyer — des images
   * noires, à petit débit, mais elle envoie —, et LiveKit obtient ce noir en
   * coupant la piste elle-même (`enabled = false`), ce qui éteindrait du même
   * coup l'aperçu de celui qui cadre. Sortie de la salle, la piste reste
   * vivante et n'atteint plus personne : `unpublishTrack` la rend à son
   * propriétaire sans l'arrêter, et c'est nous ce propriétaire.
   *
   * L'aperçu survit donc, et `camera` ne bouge pas — de son point de vue, la
   * caméra est toujours allumée, parce qu'elle l'est. Ce qui disparaît, c'est
   * la source locale de la liste de salle : plus rien n'en sort.
   *
   * Idempotent, et sans effet quand aucune caméra n'est ouverte : le rideau est
   * retenu, et la caméra allumée derrière lui restera dehors jusqu'au relevé.
   */
  setCurtain(down: boolean): Promise<void> {
    return this.serialize(() => this.applyCurtain(down));
  }

  /**
   * Prépare un lien à usage unique qui fera filmer un appareil pour ce poste.
   * Un seul lien vivant à la fois : celui qu'on remplace ne doit plus pouvoir
   * faire entrer un second appareil.
   */
  pairDevice(): Promise<VideoPairingLink> {
    return this.serialize(() => this.openPairing());
  }

  private async openAndPublish(deviceId: string | null): Promise<LocalCamera> {
    this.assertOpen();

    const generation = this.generation;
    const track = await this.openCamera(deviceId);

    // Fermé, ou la séance partie, pendant que le navigateur demandait la
    // permission : la caméra ouverte n'a plus nulle part où aller.
    if (this.interrupted(generation)) {
      track.stop();

      throw new VideoEngineClosedError();
    }

    // Le cadrage de ce poste est posé avant la publication : une webcam qu'on
    // allume dans une séance déjà cadrée ne doit pas montrer une seule image du
    // champ qu'on en avait écarté. La rappel n'a encore rien à republier — ce
    // n'est pas la caméra du moteur tant que `cameraFramed` ne la porte pas.
    const framed = new FramedCamera(track, (next) => {
      if (this.cameraFramed === framed) {
        void this.serialize(() => this.republishCamera(next));
      }
    });

    await framed.setFraming(this.localFraming());

    try {
      // Rideau baissé : la caméra s'ouvre et s'affiche chez soi, mais n'entre
      // pas en salle. C'est le cas qu'on vient chercher — brancher une
      // deuxième caméra sans que la salle la voie apparaître —, et publier
      // « juste un instant » avant de ressortir montrerait précisément ce
      // qu'on cache.
      if (!this.curtain) {
        await this.transport.publish("camera", framed.track());
      }
    } catch (error) {
      // L'ancienne caméra, s'il y en a une, reste en salle : le transport ne la
      // retire pas pour en publier une autre, il en change l'image.
      track.stop();

      throw error;
    }

    // Fermé, ou la séance partie, pendant la publication : rien ne doit rester
    // allumé pour une salle qui n'est plus là.
    if (this.interrupted(generation)) {
      framed.stop();
      track.stop();

      throw new VideoEngineClosedError();
    }

    const previous = this.cameraTrack;
    const previousFramed = this.cameraFramed;
    const camera: LocalCamera = {
      // La vignette regarde la piste **publiée**, cadrage compris : au repos,
      // elle montre ce que la salle reçoit, et rien d'autre.
      stream: new MediaStream([framed.track()]),
      // Et la caméra entière, pour cadrer dessus : ses pixels sont déjà ici,
      // et ils n'iront pas plus loin.
      source: new MediaStream([track]),
      deviceId: track.getSettings().deviceId ?? null,
      label: track.label === "" ? "Webcam" : track.label,
    };

    previousFramed?.stop();
    this.cameraFramed = framed;
    this.cameraTrack = track;
    this._camera.set(camera);

    if (previous !== null && previous !== track) {
      previous.stop();
    }

    // Débranchée, ou la permission retirée : l'aperçu ne doit pas rester figé
    // sur la dernière image.
    track.addEventListener("ended", () => {
      if (this.cameraTrack === track) {
        void this.stopCamera();
      }
    });

    return camera;
  }

  private async unpublishCamera(): Promise<void> {
    const track = this.cameraTrack;

    if (track === null) {
      return;
    }

    this.cameraTrack = null;
    this._camera.set(null);
    await this.transport.unpublish("camera").catch(() => undefined);
    track.stop();
  }

  /**
   * La moitié sérialisée de {@link setCurtain} : la caméra sort de la salle, ou
   * y rentre, sans jamais s'arrêter.
   *
   * Une caméra allumée pendant que le rideau est baissé ne le sait pas : c'est
   * `openAndPublish` qui l'a mise en salle. Le relevé la retrouve, et la chute
   * la sortira — voir la garde de `startCamera` dans `LiveSession`.
   */
  private async applyCurtain(down: boolean): Promise<void> {
    if (this.curtain === down) {
      return;
    }

    this.curtain = down;

    const track = this.cameraTrack;

    if (track === null) {
      return;
    }

    if (down) {
      // Pas de `track.stop()`, à la différence de `unpublishCamera` : la piste
      // reste à nous, et c'est elle que l'aperçu regarde.
      await this.transport.unpublish("camera").catch(() => undefined);

      return;
    }

    await this.transport.publish("camera", this.cameraFramed?.track() ?? track).catch(() => undefined);
  }

  /** Éteint la caméra que tient le moteur, sans rien demander à la salle. */
  private dropCamera(): void {
    const track = this.cameraTrack;

    this.cameraFramed?.stop();
    this.cameraFramed = null;
    this.cameraTrack = null;
    this._camera.set(null);
    track?.stop();
  }

  private async openPairing(): Promise<VideoPairingLink> {
    this.assertOpen();

    const port = this.pairingPort;

    if (port === null) {
      throw new Error("Ce contexte ne permet pas d'appairer un appareil.");
    }

    this.cancelPairing();

    const generation = this.generation;
    const { code, expiresAt } = await port.create();

    // Un lien préparé pour une séance partie ferait entrer un appareil dans une
    // salle où personne ne l'attend.
    if (this.interrupted(generation)) {
      void port.revoke(code).catch(() => undefined);

      throw new VideoEngineClosedError();
    }

    const link: VideoPairingLink = {
      url: cameraPairingUrl(this.origin(), code, port.scope),
      code,
      expiresAtMs: expiresAt * 1000,
    };

    this._pairing.set(link);

    return link;
  }

  /**
   * Retire le lien affiché, s'il y en a un. Sans attendre : il quitte l'écran
   * tout de suite, et un échec laisse au pire un code qui meurt seul en dix
   * minutes.
   */
  cancelPairing(): void {
    const link = this._pairing.value;

    if (link === null) {
      return;
    }

    this._pairing.set(null);
    void this.pairingPort?.revoke(link.code).catch((cause: unknown) => {
      console.warn("Retrait du lien d'appairage :", cause);
    });
  }

  /**
   * Coupe un appareil appairé, par le service de séance. Faux s'il ne l'a pas
   * fait, ou si la source n'est pas un appareil appairé ; vrai pour une source
   * déjà sortie de la salle, qui n'a plus rien à couper.
   *
   * Une seule coupure par appareil. La règle d'un contexte, relue à chaque
   * arrivée, et un « Couper » peuvent viser le même : au second appel, le service
   * ne le trouverait plus et refuserait (404), et ce refus se dirait pour un
   * appareil bel et bien parti. Une coupure en cours, ou réussie tant que
   * l'appareil est encore listé, rend donc sa première réponse. Une coupure
   * refusée s'oublie, pour qu'on puisse la retenter.
   */
  remove(sourceId: string): Promise<boolean> {
    const pending = this.removals.get(sourceId);

    if (pending !== undefined) {
      return pending;
    }

    const source = this._sources.value.find((candidate) => candidate.id === sourceId);

    if (source === undefined) {
      return Promise.resolve(true);
    }

    if (source.device !== "paired" || this.control === null) {
      return Promise.resolve(false);
    }

    const removal = this.control.removeDevice(source.participant).catch(() => false);

    this.removals.set(sourceId, removal);
    void removal.then((ok) => {
      if (!ok && this.removals.get(sourceId) === removal) {
        this.removals.delete(sourceId);
      }
    });

    return removal;
  }

  /**
   * Pose les cadrages publiés par la séance.
   *
   * Le moteur n'en retient qu'une chose : **le sujet**. Le reste — la découpe,
   * le quart de tour, l'objectif — se fait sur l'appareil qui filme, avant son
   * encodeur, et n'arrive donc jamais jusqu'ici (voir
   * `CameraFraming`).
   *
   * # Le seul endroit où le sujet se dit
   *
   * Il a voyagé aussi dans le nom de la piste, qui se fixe à la publication :
   * en changer depuis le studio demandait de republier, et la table disait
   * déjà la même chose sans rien republier. Deux endroits pour un même fait,
   * c'était une chance de les voir se contredire — et ils se contredisaient :
   * la liste des sources montrait le sujet de la table pendant que la retenue
   * se calculait sur le nom de la piste (constaté le 2026-09-21). Il ne reste
   * que la table, et une caméra dont elle ne dit rien filme des mains (voir
   * `subjectIn`).
   *
   * Le sujet décide de la retenue des images : en changer déplace le calage
   * d'une source, d'où le `refresh`. Le saut se voit, et c'est juste.
   */
  setFramings(cameras: CameraFramings): void {
    const reproject = !sameSubjects(this.framings, cameras);

    this.framings = cameras;

    // La webcam de ce poste, elle, suit **tout** le cadrage : le zoom et le
    // quart de tour se font ici, avant l'encodeur, exactement comme sur un
    // téléphone appairé. D'où cette pose hors de la garde ci-dessous, qui ne
    // regarde que les sujets.
    void this.cameraFramed?.setFraming(this.localFraming());

    if (!reproject) {
      return;
    }

    const sources = this.withSubjects(this.incoming);

    this._sources.set(sources);
    this.syncFollowed(sources);
    this.transport.refresh();
  }

  /** Le cadrage de la webcam de ce poste, rangé sous son identité dans la salle. */
  private localFraming(): CameraFraming {
    const identity = this.transport.localIdentity();

    return (identity === null ? undefined : this.framings[identity]) ?? DEFAULT_FRAMING;
  }

  /**
   * Republie la webcam de ce poste après que le cadrage a changé sa piste :
   * on entre dans la découpe, ou on en sort.
   *
   * `publish` sur un poste qui filme déjà fait un `replaceTrack` (voir
   * `livekit-video.ts`) : les spectateurs gardent leur élément vidéo, et seule
   * l'image change. Rien à faire sous le rideau — le relevé republiera la
   * piste du moment.
   */
  private async republishCamera(track: MediaStreamTrack): Promise<void> {
    const camera = this._camera.value;

    if (camera === null || this.curtain) {
      return;
    }

    await this.transport.publish("camera", track).catch(() => undefined);

    this._camera.set({ ...camera, stream: new MediaStream([track]) });
  }

  /**
   * Les sources de la salle, chacune avec ce qu'elle filme selon la séance.
   *
   * Le flux reste celui que le transport a donné : c'est lui que tient
   * l'élément vidéo d'une vignette, et un flux neuf lui ferait recommencer son
   * décodage.
   */
  private withSubjects(sources: readonly TransportSource[]): readonly VideoSource[] {
    return sources.map((source) => ({ ...source, subject: subjectIn(this.framings, source.participant) }));
  }

  /**
   * La qualité à recevoir pour une source ; `null` rend la valeur par défaut.
   *
   * **À appeler par ce qui montre l'image**, parce que c'est la seule chose qui
   * connaisse sa taille. Une vignette de coin se contente de la couche du
   * milieu — 640×360 ; la même image étalée sur tout le rouleau demande la
   * haute, sans quoi on agrandit du 360p et l'image est floue. Le 2026-09-20,
   * personne n'appelait cette méthode et tout le monde recevait `medium`.
   *
   * Elle coûte de la bande passante à qui la demande, et à personne d'autre :
   * c'est un abonnement, pas une republication.
   */
  watch(sourceId: string, quality: VideoQuality | null): void {
    const next = quality ?? this.defaultQuality;

    if (this.qualities.get(sourceId) === next) {
      return;
    }

    this.qualities.set(sourceId, next);
    this.transport.refresh();
  }

  /**
   * L'horloge de séance et le délai que ce poste applique aux notes des autres,
   * posés sur le calage de la séance : le jeu des autres est retenu de ce délai
   * — leur parole aussi, sauf si la séance la fait passer au plus tôt —, et rien
   * de ce poste n'est retenu.
   */
  setPlayout(playout: MediaPlayout): void {
    this.timing.setPlayout(playout);
  }

  /** Ce que le moteur applique à chaque source reçue, pour la sonde de débogage. */
  diagnostics(): VideoPlayoutDiagnostic[] {
    return this._sources.value
      .filter((source) => !source.local)
      .map((source) => ({
        id: source.id,
        subject: source.subject,
        content: videoContent(source.subject),
        holdMs: this.holdFor(source),
        latencyMs: this.transport.latencyOf(source.id),
      }));
  }

  /**
   * Lâche ce que la séance tenait — la caméra, le lien affiché, les sources —
   * sans fermer le moteur. À la déconnexion, voulue ou subie : la salle s'en
   * va, et rien ne doit rester allumé ni ouvert pour elle. Le lien est révoqué :
   * il ferait entrer un appareil dans une salle où personne ne l'attend.
   */
  release(): void {
    this.generation += 1;
    // Le rideau appartient à la séance qui s'en va : la suivante entre à
    // l'antenne, comme une séance neuve.
    this.curtain = false;
    this.dropCamera();
    this.cancelPairing();
    this.incoming = [];
    // Les cadrages appartiennent à la séance qui s'en va : la suivante repart
    // de rien, et ses caméras filment des mains tant qu'elle n'a rien dit.
    this.framings = {};
    this._sources.set([]);
    this.syncFollowed([]);
    this.holds.clear();
    this.seenDevices.clear();
    this.removals.clear();
  }

  dispose(): void {
    if (this.closed) {
      return;
    }

    this.closed = true;
    this.release();
    this.detach();
    this.stopTiming();

    if (this.ownsTiming) {
      this.timing.dispose();
    }

    this.deviceListeners.clear();
    this._sources.dispose();
    this._camera.dispose();
    this._pairing.dispose();
  }

  /**
   * Un geste à la fois. Deux `startCamera` qui se chevauchent publieraient deux
   * pistes, un `stopCamera` pendant une publication laisserait la caméra
   * allumée après « Couper », et deux `pairDevice` laisseraient un code vivant.
   * Chaque geste attend la fin du précédent, qu'il ait abouti ou non.
   */
  private serialize<T>(gesture: () => Promise<T>): Promise<T> {
    const run = this.gestures.then(gesture, gesture);

    this.gestures = run.catch(() => undefined);

    return run;
  }

  private receive(next: readonly TransportSource[]): void {
    this.incoming = next;

    const sources = this.withSubjects(next);
    const self = this.self;
    const present = new Set(next.map((source) => source.id));
    const arrived: VideoSource[] = [];

    for (const source of sources) {
      if (source.device !== "paired" || this.seenDevices.has(source.id)) {
        continue;
      }

      this.seenDevices.add(source.id);

      if (self !== null && source.owner === self) {
        arrived.push(source);
      }
    }

    for (const id of Array.from(this.holds.keys())) {
      if (!present.has(id)) {
        this.holds.delete(id);
      }
    }

    // Une coupure ne vaut que tant que l'appareil est là : sorti, il n'a plus
    // rien à attendre d'elle.
    for (const id of Array.from(this.removals.keys())) {
      if (!present.has(id)) {
        this.removals.delete(id);
      }
    }

    this._sources.set(sources);
    this.syncFollowed(sources);

    if (arrived.length === 0) {
      return;
    }

    // Le code est à usage unique : un appareil de ce poste qui entre l'a consommé.
    this._pairing.set(null);

    for (const source of arrived) {
      for (const listener of this.deviceListeners) {
        listener(source);
      }
    }
  }

  /**
   * Les visages des autres se font suivre par le calage : quand la parole passe
   * au plus tôt, ce qu'ils mettent à se montrer dit ce que la voix de leur
   * compte attend. Un visage coupé à la source ne montre plus rien : sa dernière
   * mesure ne fait attendre personne.
   */
  private syncFollowed(next: readonly VideoSource[]): void {
    const faces = new Map<string, VideoSource>();

    for (const source of next) {
      if (videoContent(source.subject) === "speech" && !source.muted && !this.ownedHere(source)) {
        faces.set(source.id, source);
      }
    }

    for (const [id, followed] of Array.from(this.followed)) {
      if (faces.get(id)?.owner !== followed.owner) {
        this.followed.delete(id);
        followed.unfollow();
      }
    }

    for (const [id, source] of faces) {
      if (this.followed.has(id)) {
        continue;
      }

      this.followed.set(id, {
        owner: source.owner,
        unfollow: this.timing.follow({ id, owner: source.owner, naturalMs: () => this.transport.latencyOf(id) }),
      });
    }
  }

  /**
   * Suit le calage : l'horloge part au transport, et une retenue qui change se
   * réapplique. Le délai des notes et le calage de la parole touchent toutes les
   * sources ; une cible de parole ne touche qu'aux visages, et le transport n'est
   * sollicité que si l'un d'eux doit bouger — réappliquer, c'est aussi
   * redemander à la salle la qualité de chaque source.
   */
  private retime(change: MediaTimingChange): void {
    if (change.clock) {
      this.transport.setClock(this.timing.playout.clockOffsetMs);
    }

    if (change.notes || (change.speech && this.holdsMoved())) {
      this.transport.refresh();
    }
  }

  /** Une source doit-elle changer de retenue depuis que le transport l'a demandée ? */
  private holdsMoved(): boolean {
    return this._sources.value.some((source) => this.holdFor(source) !== (this.holds.get(source.id) ?? 0));
  }

  /** La retenue que le transport pose sur une source, gardée pour savoir quand la réappliquer. */
  private applyHold(source: TransportSource): number {
    const hold = this.holdFor(source);

    this.holds.set(source.id, hold);

    return hold;
  }

  /**
   * On se regarde en direct ; les autres, avec le délai de leurs notes — ou,
   * pour un visage quand la parole passe au plus tôt, calés sur leur voix.
   *
   * Ce que filme la source se relit dans la table, et non sur l'objet reçu :
   * le transport demande ses retenues avec les sources qu'il connaît, qui n'en
   * disent rien.
   */
  private holdFor(source: TransportSource): number {
    if (this.ownedHere(source)) {
      return 0;
    }

    return this.timing.holdFor(videoContent(subjectIn(this.framings, source.participant)), source.owner, source.id);
  }

  /** Cette source est-elle à ce poste : publiée ici, ou par un appareil ou un onglet du même compte. */
  private ownedHere(source: TransportSource): boolean {
    const self = this.self;

    return source.local || (self !== null && source.owner === self);
  }

  private assertOpen(): void {
    if (this.closed) {
      throw new VideoEngineClosedError();
    }
  }

  /** Le moteur a-t-il été fermé, ou la séance lâchée, depuis le début d'un geste ? */
  private interrupted(generation: number): boolean {
    return this.closed || this.generation !== generation;
  }
}

/**
 * Deux tables de cadrage qui disent le même sujet pour les mêmes caméras.
 *
 * Le sujet seul : c'est tout ce que le moteur lit d'un cadrage, et l'état de
 * séance se republie pour bien d'autres raisons — une position, une audience.
 * Comparer les tables entières ferait reprojeter les sources à chaque zoom
 * qu'un téléphone applique tout seul de son côté.
 */
function sameSubjects(a: CameraFramings, b: CameraFramings): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);

  for (const key of keys) {
    if (subjectIn(a, key) !== subjectIn(b, key)) {
      return false;
    }
  }

  return true;
}
